import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Ticket,
  Calendar,
  MapPin,
  Clock,
  ArrowLeft,
  Copy,
  Check,
  Loader,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";
import { NonPaginatedResponse } from "../../types";
import { IEventProps } from "../../types/Event";
import { FullPageLoader } from "../../components/ui/loader";
import { useUser } from "../../context/UserContext";
import { getOrgTokenKey } from "../../context/UserContext";
import { getCurrencySymbol } from "../../lib/data/currencies";
import { isCustomDomain } from "../../lib/utils/domain";

interface TicketData {
  id: number;
  event: number;
  user: number;
  ticket_type: string;
  unique_code: string;
  price: string;
  currency: string;
}

interface EventWithTickets {
  event: IEventProps;
  tickets: TicketData[];
}

const MyTickets: React.FC = () => {
  const navigate = useNavigate();
  const { organizationSlug: urlOrganizationSlug } = useParams<{ organizationSlug?: string }>();
  const { userProfile } = useUser();
  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [eventsWithTickets, setEventsWithTickets] = useState<
    EventWithTickets[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [loadingEvents, setLoadingEvents] = useState<Set<number>>(new Set());

  // Get organization slug - from URL if available, otherwise from user profile (for custom domains)
  const organizationSlug = useMemo(() => {
    if (urlOrganizationSlug) {
      return urlOrganizationSlug;
    }
    // If on custom domain, get slug from user profile
    if (isCustomDomain() && userProfile?.organizationSlug) {
      return userProfile.organizationSlug;
    }
    return "";
  }, [urlOrganizationSlug, userProfile]);

  useEffect(() => {
    if (organizationSlug) {
      fetchTickets();
    }
  }, [userProfile, organizationSlug]);

  const fetchTickets = async () => {
    if (!organizationSlug) {
      toast.error("Organization information is missing");
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      // Get organization slug from user profile

      const tokenKey = getOrgTokenKey(organizationSlug);
      const token = localStorage.getItem(tokenKey);

      if (!token) {
        toast.error("Please log in to view your tickets");
        navigate("/");
        return;
      }

      const response = await PerformRequest<NonPaginatedResponse<TicketData[]>>(
        {
          method: Endpoints.GetAttendeeTickets().method,
          route: Endpoints.GetAttendeeTickets().route,
          doNotUseAuthorization: false,
          organizationSlug: organizationSlug,
        }
      );

      if (response.status === HttpCodes.Success) {
        // Response structure: { status, data: { data: TicketData[] } }
        const ticketData =
          ((response.data?.data as any)?.data as TicketData[]) || [];
        console.log("ticketData:", ticketData);

        if (!Array.isArray(ticketData) || ticketData.length === 0) {
          setTickets([]);
          setEventsWithTickets([]);
          setIsLoading(false);
          return;
        }

        setTickets(ticketData);

        // Group tickets by event ID and IMMEDIATELY fetch events
        const eventIds = Array.from(new Set(ticketData.map((t) => t.event)));
        console.log("Event IDs to fetch:", eventIds);
      } else {
        toast.error(response.message || "Failed to load tickets");
      }
    } catch (error: any) {
      console.error("Error fetching tickets:", error);
      toast.error(error.message || "Failed to load tickets");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEventsForTickets = async (
    eventIds: number[],
    ticketData: TicketData[]
  ) => {
    if (eventIds.length === 0) {
      setEventsWithTickets([]);
      return;
    }

    setLoadingEvents(new Set(eventIds));

    try {
      const eventPromises = eventIds.map(async (eventId) => {
        try {
          const response = await PerformRequest<
            NonPaginatedResponse<{
              event: IEventProps;
              ticket_sales?: any[];
            }>
          >({
            method: Endpoints.GetSingleEvent(eventId.toString()).method,
            route: Endpoints.GetSingleEvent(eventId.toString()).route,
            doNotUseAuthorization: true,
          });

          // Response structure: { status, data: { event: IEventProps, ... } }
          if (
            response &&
            response.status === HttpCodes.Success &&
            response.data
          ) {
            const eventData = response.data?.data;
            if (eventData.event) {
              const eventTickets = ticketData.filter(
                (t) => t.event === eventId
              );

              return {
                event: eventData.event,
                tickets: eventTickets,
              };
            }
          }

          return null;
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(eventPromises);

      const validResults = results.filter(
        (r): r is EventWithTickets => r !== null
      );

      // Sort by event date (upcoming first)
      validResults.sort((a, b) => {
        const dateA = new Date(a.event.date).getTime();
        const dateB = new Date(b.event.date).getTime();
        return dateA - dateB;
      });

      setEventsWithTickets(validResults);
    } catch (error) {
      toast.error("Failed to load event details");
    } finally {
      setLoadingEvents(new Set());
    }
  };

  useEffect(() => {
    if (tickets && tickets.length > 0) {
      const eventIds = Array.from(new Set(tickets.map((t) => t.event)));

      fetchEventsForTickets(eventIds, tickets);
    }
  }, [tickets]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Ticket code copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "TBD";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes || "00"} ${ampm}`;
    } catch {
      return "TBD";
    }
  };

  const getLocationString = (event: IEventProps) => {
    if (event.location) {
      const parts = [
        event.location.address,
        event.location.city,
        event.location.state,
        event.location.country,
      ].filter(Boolean);
      return parts.join(", ") || "Location TBA";
    }
    return event.type === "online"
      ? "Online Event"
      : event.type === "hybrid"
      ? "Hybrid Event"
      : "Location TBA";
  };

  // Show loading state until both tickets and events are loaded
  const isFullyLoaded = !isLoading && loadingEvents.size === 0;

  if (!isFullyLoaded) {
    return <FullPageLoader label="Loading your tickets..." />;
  }

  if (eventsWithTickets.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#17265a] hover:text-[#0f1a3f] transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </motion.button>

          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Ticket className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-[#17265a] mb-4">
              No Tickets Yet
            </h2>
            <p className="text-gray-600 mb-8">
              You haven't purchased any tickets yet. Start exploring events to
              get started!
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-[#17265a] text-white rounded-lg hover:bg-[#0f1a3f] transition-colors"
            >
              Browse Events
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#17265a] hover:text-[#0f1a3f] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </motion.button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-[#17265a] mb-2">
                My Tickets
              </h1>
              <p className="text-gray-600">
                {tickets.length} ticket{tickets.length !== 1 ? "s" : ""} across{" "}
                {eventsWithTickets.length} event
                {eventsWithTickets.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Events List */}
        <div className="space-y-6">
          {eventsWithTickets.map((eventGroup, eventIndex) => {
            const { event, tickets: eventTickets } = eventGroup;
            const totalPrice = eventTickets.reduce(
              (sum, ticket) => sum + parseFloat(ticket.price),
              0
            );

            return (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: eventIndex * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow"
              >
                {/* Event Header */}
                <div className="relative h-48 overflow-hidden">
                  {event.cover_image ? (
                    <img
                      src={event.cover_image}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#17265a] to-[#1f2f6b]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {event.name}
                    </h2>
                    <div className="flex flex-wrap gap-4 text-white/90 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{getLocationString(event)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4" />
                        <span>
                          {eventTickets.length} ticket
                          {eventTickets.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tickets List */}
                <div className="p-6">
                  <div className="space-y-4">
                    {eventTickets.map((ticket, ticketIndex) => (
                      <motion.div
                        key={ticket.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: ticketIndex * 0.05 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          const orgSlug =
                            userProfile?.organizationSlug || organizationSlug;
                          if (orgSlug) {
                            navigate(`/event/${orgSlug}/${event.id}`);
                          } else {
                            navigate(`/event/${event.id}`);
                          }
                        }}
                        className="bg-gradient-to-br from-[#17265a] to-[#1f2f6b] rounded-xl p-5 text-white border-2 border-[#17265a]/20 cursor-pointer hover:border-white/40 transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center border border-white/20">
                                <Ticket className="w-5 h-5" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold">
                                  {ticket.ticket_type}
                                </h3>
                                <p className="text-white/70 text-sm">
                                  Ticket #{ticket.id}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold">
                              {getCurrencySymbol(ticket.currency)}{parseFloat(ticket.price).toFixed(2)}
                            </p>
                            <p className="text-white/60 text-xs">
                              {ticket.currency}
                            </p>
                          </div>
                        </div>

                        <div className="border-t border-white/20 pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-white/80 text-xs mb-1">
                                Unique Code
                              </p>
                              <p className="font-mono text-base font-semibold">
                                {ticket.unique_code}
                              </p>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyCode(ticket.unique_code);
                              }}
                              className="ml-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2 backdrop-blur-sm border border-white/20"
                            >
                              {copiedCode === ticket.unique_code ? (
                                <>
                                  <Check className="w-4 h-4" />
                                  <span className="text-sm">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4" />
                                  <span className="text-sm">Copy</span>
                                </>
                              )}
                            </motion.button>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                          <p className="text-white/70 text-sm">
                            Click to view event details
                          </p>
                          <ExternalLink className="w-4 h-4 text-white/70" />
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Event Summary */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Total for this event
                        </p>
                        <p className="text-xl font-bold text-[#17265a]">
                          {eventTickets.length > 0 && eventTickets[0].currency
                            ? getCurrencySymbol(eventTickets[0].currency)
                            : "$"}{totalPrice.toFixed(2)} ({eventTickets.length} ticket
                          {eventTickets.length !== 1 ? "s" : ""})
                        </p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const orgSlug = userProfile?.organizationSlug;
                          if (orgSlug) {
                            navigate(`/event/${orgSlug}/${event.id}`);
                          } else {
                            navigate(`/event/${event.id}`);
                          }
                        }}
                        className="px-4 py-2 bg-[#17265a] text-white rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Event</span>
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MyTickets;
